export const PAYIN = [
  {
    group: "Overview",
    color: "bg-[#1E293B]",
    items: [
      {
        method: "INFO",
        path: "-",
        title: "Overview",
        desc: "Use the PayIn API to collect UPI payments from your customers.",
        headers: null,
        request: null,
        response: `Customers can pay using:\n- Google Pay\n- PhonePe\n- Paytm\n- Any UPI app`
      },
      {
        method: "FLOW",
        path: "-",
        title: "Payment Flow",
        desc: "Complete payment lifecycle",
        headers: null,
        request: null,
        response: `1. Create a PayIn request\n2. A UPI link / QR code is generated\n3. Customer completes the payment\n4. Wallet is credited\n5. Callback is triggered`
      },
      {
        method: "START",
        path: "-",
        title: "Getting Started",
        desc: "Steps to get started with the API",
        headers: null,
        request: null,
        response: `1. Register your account\n2. Complete KYC\n3. Get admin approval\n4. Enable PayIn service\n5. Whitelist your IP\n6. Use your API key`
      },
      {
        method: "AUTH",
        path: "-",
        title: "Authentication",
        desc: "Send these headers with every request",
        headers: null,
        request: null,
        response: `X-Api-Key: <your_api_key>\nContent-Type: application/json`
      }
    ]
  },
  {
    group: "PayIn",
    color: "bg-[#065F46]",
    items: [
      {
        method: "POST",
        path: "/api/v1/payin/create",
        title: "Create PayIn",
        desc: "Create a new PayIn request to collect UPI payment from a customer.",
        headers: `X-Api-Key: <your_api_key>\nContent-Type: application/json`,
        request: `{\n  "order_id": "YOUR-ORDER-001",\n  "amount": 1000,\n  "customer_phone": "9876543210",\n  "customer_name": "Rahul Sharma"\n}`,
        requestFields: [
          ["order_id", "string", false, "Your unique order ID (max 100 chars). Auto-generated if not provided."],
          ["amount", "number", true, "Collection amount in ₹ (min: ₹300)"],
          ["customer_phone", "string", true, "Customer's 10-digit mobile number"],
          ["customer_name", "string", false, "Customer's name (max 100 chars)"]
        ],
        response: `{\n  "success": true,\n  "message": "PayIn initiated. Awaiting customer payment.",\n  "data": {\n    "transaction_id": "txn_xxxxxxxxxxxxxxxx",\n    "order_id":       "YOUR-ORDER-001",\n    "txn_ref":        "PAYIN-REF-XXXXX",\n    "amount":         1000.00,\n    "commission":     10.00,\n    "intent_url":     "upi://pay?pa=xxxxx&am=1000.00&tn=Payment&cu=INR",\n    "expires_in":     600,\n    "status":         "pending"\n  }\n}`,
        responseFields: [
          ["transaction_id", "KlyroPay's unique transaction ID — save this for status checks"],
          ["txn_ref", "Payment reference number"],
          ["intent_url", "UPI payment link — use to generate QR or redirect the customer"],
          ["expires_in", "Expiry time in seconds (600 = 10 minutes)"],
          ["commission", "Your charge — will be deducted from wallet"]
        ],
        integration: {
          title: "intent_url — How to Display to the Customer",
          options: [
            {
              name: "Option A — QR Code (Web/Desktop)",
              code: `<script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>\n\n<div id="qrcode"></div>\n\n<script>\n  // Pass intent_url via JS variable — do not HTML encode it\n  const intentUrl = document.getElementById('intent-url-input').value;\n\n  new QRCode(document.getElementById("qrcode"), {\n    text: intentUrl,\n    width: 220,\n    height: 220,\n  });\n</script>\n\n<!-- Pass the URL via a hidden input -->\n<input type="hidden" id="intent-url-input" value="{{ $intentUrl }}">`
            },
            {
              name: "Option B — UPI Deep Link (Mobile)",
              code: `<a href="{{ $intentUrl }}" class="btn-pay">\n  Pay with UPI App\n</a>`
            },
            {
              name: "Option C — Both (Recommended)",
              code: `<!-- QR for Desktop -->\n<div id="qrcode"></div>\n<p>Scan with GPay | PhonePe | Paytm | BHIM</p>\n\n<!-- Button for Mobile -->\n<button onclick="window.location.href = document.getElementById('intent-url-input').value">\n  📱 Pay with UPI App\n</button>\n\n<input type="hidden" id="intent-url-input" value="{{ $intentUrl }}">\n\n⚠️ Important: intent_url contains & characters.\n   Using {{ $url }} in Blade will encode it as &amp; which will not work in UPI apps.\n   Always pass via a hidden input or JS variable.`
            }
          ]
        }
      },
      {
        method: "GET",
        path: "/api/v1/payin/status/{txn_ref}",
        title: "Payment Status Check",
        desc: "Check the current status of a transaction.",
        headers: `X-Api-Key: <your_api_key>`,
        request: null,
        response: `{\n  "success": true,\n  "message": "Status retrieved.",\n  "data": {\n    "transaction_id": "txn_xxxxxxxxxxxxxxxx",\n    "order_id":       "YOUR-ORDER-001",\n    "txn_ref":        "PAYIN-REF-XXXXX",\n    "amount":         1000.00,\n    "commission":     10.00,\n    "customer_phone": "9876543210",\n    "customer_name":  "Rahul Sharma",\n    "utr":            "bank_utr_number",\n    "status":         "success",\n    "created_at":     "2024-04-30T10:00:00.000000Z"\n  }\n}`,
        polling: `const txnRef = 'PAYIN-REF-XXXXX';\nconst apiKey = '<your_api_key>';\nconst terminal = ['success', 'failed', 'expired'];\n\n// Exponential backoff — reduces server load\nconst delays = [5, 5, 5, 10, 10, 30, 30, 60]; // seconds\nlet step = 0;\n\nasync function checkStatus() {\n  try {\n    const res = await fetch(\`/api/v1/payin/status/\${txnRef}\`, {\n      headers: { 'X-Api-Key': apiKey }\n    });\n    const data = await res.json();\n\n    if (terminal.includes(data.data.status)) {\n      console.log('Final status:', data.data.status);\n      console.log('UTR:', data.data.utr);\n      return; // stop polling\n    }\n  } catch (e) {\n    console.error('Poll error:', e);\n  }\n\n  if (step < delays.length) {\n    setTimeout(checkStatus, delays[step++] * 1000);\n  }\n}\n\n// First check after 5 seconds\nsetTimeout(checkStatus, delays[step++] * 1000);`
      },
      {
        method: "GET",
        path: "/api/v1/payin/transactions",
        title: "Transaction History",
        desc: "All PayIn transactions with pagination.",
        headers: `X-Api-Key: <your_api_key>`,
        request: `?page=1&limit=15`,
        queryParams: [
          ["page", "Current page number", "1"],
          ["limit", "Items per page", "15"]
        ],
        response: `{\n  "success": true,\n  "data": {\n    "transactions": [\n      {\n        "transaction_id": "txn_xxxxxxxxxxxxxxxx",\n        "order_id":       "YOUR-ORDER-001",\n        "txn_ref":        "PAYIN-REF-XXXXX",\n        "amount":         1000.00,\n        "commission":     10.00,\n        "customer_phone": "9876543210",\n        "customer_name":  "Rahul Sharma",\n        "utr":            "bank_utr_number",\n        "status":         "success",\n        "created_at":     "2024-04-30T10:00:00.000000Z"\n      }\n    ],\n    "pagination": {\n      "total":        100,\n      "per_page":     15,\n      "current_page": 1,\n      "last_page":    7,\n      "has_more":     true\n    }\n  }\n}`
      },
      {
        method: "GET",
        path: "/api/v1/wallet/balance",
        title: "Wallet Balance",
        desc: "Check available wallet balance.",
        headers: `X-Api-Key: <your_api_key>`,
        request: null,
        response: `{\n  "success": true,\n  "data": {\n    "balance":        9990.00,\n    "locked_balance": 0.00\n  }\n}`
      }
    ]
  },
  {
    group: "Callback / Webhook",
    color: "bg-[#B45309]",
    items: [
      {
        method: "POST",
        path: "https://yourserver.com/your-callback-endpoint",
        title: "Payment Callback",
        desc: "KlyroPay sends a callback to your server once the payment is completed.",
        headers: `Content-Type: application/json\nX-KlyroPay-Event: transaction.updated\nX-KlyroPay-Signature: <hmac_signature>`,
        request: null,
        response: `{\n  "event":          "transaction.updated",\n  "transaction_id": "txn_xxxxxxxxxxxxxxxx",\n  "reference_id":   "PAYIN-REF-XXXXX",\n  "service":        "payin",\n  "status":         "success",\n  "amount":         1000.00,\n  "commission":     10.00,\n  "net_amount":     990.00,\n  "order_id":       "YOUR-ORDER-001",\n  "txn_ref":        "PAYIN-REF-XXXXX",\n  "utr":            "bank_utr_number",\n  "timestamp":      "2024-04-30T10:05:00.000000Z"\n}\n\n// NOTE: Your server must return 200 — otherwise the callback will be retried.`,
        callbackFields: [
          ["utr", "Bank UTR — proof of payment"],
          ["order_id", "Your original order ID"],
          ["net_amount", "Amount credited to wallet (amount - commission)"],
          ["txn_ref", "Payment reference number"]
        ],
        verification: `// PHP Example\n$receivedSig = $_SERVER['HTTP_X_KLYROPAY_SIGNATURE'];\n$payload = json_decode(file_get_contents('php://input'), true);\n\n$expected = hash_hmac('sha256', json_encode($payload), '<your_api_key>');\n\nif (!hash_equals($expected, $receivedSig)) {\n  http_response_code(401);\n  exit;\n}\n\nif ($payload['status'] === 'success') {\n  $utr      = $payload['utr'];        // bank UTR\n  $credited = $payload['net_amount']; // amount credited to wallet\n  $orderId  = $payload['order_id'];   // your order ID\n  // update your order here\n}\n\nhttp_response_code(200);\necho json_encode(['status' => 'ok']);\n\n// Node.js\nconst crypto      = require('crypto');\nconst receivedSig = req.headers['x-klyropay-signature'];\nconst expected    = crypto\n  .createHmac('sha256', '<your_api_key>')\n  .update(JSON.stringify(req.body))\n  .digest('hex');\n\nif (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(receivedSig))) {\n  return res.status(401).send('Unauthorized');\n}\n\nconst { status, utr, net_amount, order_id } = req.body;\n// update your order here\nres.status(200).json({ status: 'ok' });\n\n// NOTE: Replace <your_api_key> with your actual API key.`
      }
    ]
  },
  {
    group: "Status Info",
    color: "bg-[#4B2364]",
    items: [
      {
        method: "INFO",
        path: "-",
        title: "Transaction Status Values",
        desc: "Different possible statuses of a PayIn transaction.",
        headers: null,
        request: null,
        response: `pending  -> Customer has not paid yet\nsuccess  -> Payment received — wallet has been credited\nfailed   -> Payment failed\nexpired  -> No payment received within 10 minutes`
      }
    ]
  },
  {
    group: "Rules & Limits",
    color: "bg-[#6D28D9]",
    items: [
      {
        method: "INFO",
        path: "-",
        title: "System Rules",
        desc: "Important limits and rules for PayIn API.",
        headers: null,
        request: null,
        response: `Minimum amount: ₹300\nPayment expiry: 10 minutes\nWallet credit: amount - commission\nCommission: Based on your assigned plan\nCallback trigger: success / failed / expired\nCallback retry: 5 attempts, 5 min interval`
      }
    ]
  },
  {
    group: "Error Reference",
    color: "bg-[#7C2D12]",
    items: [
      {
        method: "INFO",
        path: "-",
        title: "Errors",
        desc: "Common API errors.",
        headers: null,
        request: null,
        response: [
          ["401", "API key is missing", "X-Api-Key header was not sent"],
          ["401", "Invalid API key", "The provided key is incorrect"],
          ["401", "API key inactive/expired", "The key has expired or been deactivated"],
          ["403", "No IP whitelist", "No IP address has been approved"],
          ["403", "IP not whitelisted", "Calls from this IP are not allowed"],
          ["422", "Validation failed", "Error in request fields"],
          ["422", "Service inactive", "Please contact admin"],
          ["429", "Rate limit exceeded", "Per-minute request limit exceeded"]
        ]
      }
    ]
  },
  {
    group: "Quick Reference",
    color: "bg-[#0F172A]",
    items: [
      {
        method: "TABLE",
        path: "-",
        title: "Quick Reference",
        desc: "Important endpoints at a glance",
        response: [
          ["POST", "/api/v1/payin/create", "Create a PayIn request"],
          ["GET", "/api/v1/payin/status/{txn_ref}", "Check payment status"],
          ["GET", "/api/v1/payin/transactions", "View transaction history"],
          ["GET", "/api/v1/wallet/balance", "Check wallet balance"]
        ]
      }
    ]
  }
];

export const PAYOUT = [
  {
    group: "Overview",
    color: "bg-[#1E293B]",
    items: [
      {
        method: "INFO",
        path: "-",
        title: "Overview",
        desc: "Use the Payout API to transfer funds directly to your users' bank accounts.",
        headers: null,
        request: null,
        response: `Direct bank transfer using IMPS`
      },
      {
        method: "FLOW",
        path: "-",
        title: "Payout Flow",
        desc: "Transfer lifecycle",
        headers: null,
        request: null,
        response: `1. Make an API call\n2. KlyroPay debits amount + commission from your wallet\n3. Bank transfer is initiated\n4. Success/Failed → Your callback URL is notified`
      },
      {
        method: "START",
        path: "-",
        title: "Getting Started",
        desc: "Setup steps",
        headers: null,
        request: null,
        response: `1. Register your KlyroPay account\n2. Complete KYC (PAN + Aadhaar)\n3. Get account approval from admin\n4. Request access to the Payout service\n5. Register your server IP + callback URL (admin will approve)\n6. Get your API Key and use it in the X-Api-Key header`
      },
      {
        method: "AUTH",
        path: "-",
        title: "Authentication",
        desc: "Send these headers with every request:",
        headers: null,
        request: null,
        response: `X-Api-Key: <your_api_key>\nContent-Type: application/json`
      }
    ]
  },
  {
    group: "Payout",
    color: "bg-[#065F46]",
    items: [
      {
        method: "POST",
        path: "/api/v1/services/payout/process",
        title: "Initiate Payout",
        desc: "IMPS transfers to the beneficiary's bank account.",
        headers: `X-Api-Key: <your_api_key>\nContent-Type: application/json`,
        request: `{\n  "mobile_no": "9876543210",\n  "beneficiary_name": "Rahul Sharma",\n  "account_no": "1234567890",\n  "ifsc": "SBIN0001234",\n  "bank_name": "State Bank of India",\n  "amount": 5000,\n  "transfer_type": "5",\n  "partner_request_id": "YOUR-UNIQUE-ORDER-ID"\n}`,
        requestFields: [
          ["mobile_no", "string", true, "10-digit mobile number"],
          ["beneficiary_name", "string", true, "Account holder name (max 100 chars)"],
          ["account_no", "string", true, "Bank account number (8–20 digits)"],
          ["ifsc", "string", true, "IFSC code (e.g. SBIN0001234)"],
          ["bank_name", "string", true, "Bank name (max 100 chars)"],
          ["amount", "number", true, "Transfer amount ₹1000 – ₹2,00,000"],
          ["transfer_type", "string", true, "Always '5' (IMPS)"],
          ["partner_request_id", "string", false, "Your unique order ID (auto-generated if not provided; used for duplicate check)"]
        ],
        response: `// Success\n{\n  "success": true,\n  "message": "Payout transferred successfully.",\n  "data": {\n    "transaction": {\n      "transaction_id":     "txn_xxxxxxxxxxxxxxxx",\n      "reference_id":       "bank_reference_number",\n      "partner_request_id": "YOUR-UNIQUE-ORDER-ID",\n      "status":             "success",\n      "amount":             5000.00,\n      "charge":             10.00,\n      "net_amount":         5010.00\n    }\n  }\n}\n\n// Pending\n{\n  "success": true,\n  "message": "Payout is being processed. You will be notified once settled.",\n  "data": {\n    "transaction": {\n      "transaction_id":     "txn_xxxxxxxxxxxxxxxx",\n      "reference_id":       null,\n      "partner_request_id": "YOUR-UNIQUE-ORDER-ID",\n      "status":             "pending",\n      "amount":             5000.00,\n      "charge":             10.00,\n      "net_amount":         5010.00\n    }\n  }\n}\n// NOTE: A pending status means the bank is still processing. Final status will be sent via callback.\n\n// Failed / Refunded\n{\n  "success": false,\n  "message": "Payout failed. Amount has been refunded to your wallet.",\n  "data": {\n    "transaction": {\n      "transaction_id": "txn_xxxxxxxxxxxxxxxx",\n      "status":         "refunded",\n      "amount":         5000.00,\n      "charge":         10.00,\n      "net_amount":     5010.00\n    }\n  }\n}`,
        responseFields: [
          ["transaction_id", "KlyroPay's unique transaction ID — save this"],
          ["reference_id", "Bank reference number (available after success)"],
          ["partner_request_id", "Your order ID"],
          ["amount", "Transfer amount"],
          ["charge", "Commission charged"],
          ["net_amount", "Total wallet deduction (amount + charge)"]
        ]
      },
      {
        method: "GET",
        path: "/api/v1/wallet/balance",
        title: "Wallet Balance",
        desc: "Check available payout balance.",
        headers: `X-Api-Key: <your_api_key>`,
        request: null,
        response: `{\n  "success": true,\n  "message": "Wallet balance retrieved successfully.",\n  "data": {\n    "balance":        45000.00,\n    "locked_balance": 0.00\n  }\n}`,
        responseFields: [
          ["balance", "Available balance — ready for payout"],
          ["locked_balance", "Amount on hold — not available for use"]
        ]
      },
      {
        method: "GET",
        path: "/api/v1/transactions/service/payout",
        title: "Payout Transactions",
        desc: "Retrieve all payout transactions with filters.",
        headers: `X-Api-Key: <your_api_key>`,
        request: `?status=success&page=1&limit=15`,
        queryParams: [
          ["status", "Filter by status (success / pending / failed / refunded)", "All"],
          ["from_date", "Start date — YYYY-MM-DD", "-"],
          ["to_date", "End date — YYYY-MM-DD", "-"],
          ["page", "Page number", "1"],
          ["limit", "Records per page (max: 100)", "15"]
        ],
        response: `{\n  "success": true,\n  "data": {\n    "transactions": [\n      {\n        "transaction_id":     "txn_xxxxxxxxxxxxxxxx",\n        "reference_id":       "bank_reference_number",\n        "partner_request_id": "YOUR-UNIQUE-ORDER-ID",\n        "amount":             5000.00,\n        "charge":             10.00,\n        "net_amount":         5010.00,\n        "status":             "success",\n        "created_at":         "2024-04-16T10:30:00.000000Z",\n        "details": {\n          "beneficiary_name": "Rahul Sharma",\n          "account_no":       "XXXXXX7890",\n          "ifsc":             "SBIN0001234",\n          "bank_name":        "State Bank of India"\n        }\n      }\n    ],\n    "pagination": {\n      "total":        50,\n      "per_page":     15,\n      "current_page": 1,\n      "last_page":    4,\n      "has_more":     true\n    }\n  }\n}`
      },
      {
        method: "GET",
        path: "/api/v1/transactions/service/payout/{transaction_id}",
        title: "Single Transaction",
        desc: "Fetch details of a single payout transaction.",
        headers: `X-Api-Key: <your_api_key>`,
        request: null,
        response: `{\n  "success": true,\n  "data": {\n    "transaction_id":     "txn_xxxxxxxxxxxxxxxx",\n    "reference_id":       "bank_reference_number",\n    "partner_request_id": "YOUR-UNIQUE-ORDER-ID",\n    "amount":             5000.00,\n    "charge":             10.00,\n    "net_amount":         5010.00,\n    "status":             "success",\n    "created_at":         "2024-04-16T10:30:00.000000Z"\n  }\n}`
      }
    ]
  },
  {
    group: "Callback / Webhook",
    color: "bg-[#B45309]",
    items: [
      {
        method: "POST",
        path: "https://yourserver.com/your-callback-endpoint",
        title: "Payout Callback",
        desc: "KlyroPay sends a callback to your server when the payout status is updated.",
        headers: `Content-Type: application/json\nX-KlyroPay-Event: transaction.updated\nX-KlyroPay-Signature: <signature>`,
        request: null,
        response: `{\n  "event":       "transaction.updated",\n  "transaction_id": "txn_xxxxxxxxxxxxxxxx",\n  "reference_id":   "bank_reference_number",\n  "service":        "payout",\n  "status":         "success",\n  "amount":         5000.00,\n  "commission":     10.00,\n  "net_amount":     5010.00,\n  "timestamp":      "2024-04-16T10:30:00.000000Z"\n}`,
        verifySignature: `// PHP\n$receivedSig = $_SERVER['HTTP_X_KLYROPAY_SIGNATURE'];\n$payload     = json_decode(file_get_contents('php://input'), true);\n$expected    = hash_hmac('sha256', json_encode($payload), '<your_api_key>');\n\nif (!hash_equals($expected, $receivedSig)) {\n  http_response_code(401);\n  exit;\n}\n\n// Valid request — process payout\n$status = $payload['status']; // success / failed / refunded\nhttp_response_code(200);\necho json_encode(['status' => 'ok']);\n\n// Node.js\nconst crypto      = require('crypto');\nconst receivedSig = req.headers['x-klyropay-signature'];\nconst expected    = crypto\n  .createHmac('sha256', '<your_api_key>')\n  .update(JSON.stringify(req.body))\n  .digest('hex');\n\nif (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(receivedSig))) {\n  return res.status(401).send('Unauthorized');\n}\n\n// Valid request — process payout\nres.status(200).json({ status: 'ok' });`
      }
    ]
  },
  {
    group: "Status Info",
    color: "bg-[#4B2364]",
    items: [
      {
        method: "INFO",
        path: "-",
        title: "Transaction Status Values",
        desc: "Different payout statuses.",
        headers: null,
        request: null,
        response: `success   -> Money credited to the beneficiary's account\npending   -> Bank is processing – you will receive a callback with final status\nfailed    -> Transfer failed\nrefunded  -> Transfer failed, amount refunded to your wallet`
      }
    ]
  },
  {
    group: "Error Reference",
    color: "bg-[#7C2D12]",
    items: [
      {
        method: "INFO",
        path: "-",
        title: "Errors",
        desc: "Common API errors.",
        headers: null,
        request: null,
        response: [
          ["401", "API key is missing", "X-Api-Key header was not sent"],
          ["401", "Invalid API key", "The provided key is incorrect"],
          ["401", "API key inactive/expired", "The key has expired or been deactivated"],
          ["403", "No IP whitelist", "No IP address has been approved"],
          ["403", "IP not whitelisted", "Calls from this IP are not allowed"],
          ["409", "Duplicate request", "This partner_request_id already exists"],
          ["422", "Validation failed", "Error in request fields"],
          ["422", "Insufficient balance", "Wallet balance is too low"],
          ["429", "Rate limit exceeded", "Per-minute request limit exceeded"]
        ]
      }
    ]
  },
  {
    group: "Quick Reference",
    color: "bg-[#0F172A]",
    items: [
      {
        method: "TABLE",
        path: "-",
        title: "Quick Reference",
        desc: "Important endpoints at a glance",
        response: [
          ["POST", "/api/v1/services/payout/process", "Initiate a payout"],
          ["GET", "/api/v1/wallet/balance", "Check wallet balance"],
          ["GET", "/api/v1/transactions/service/payout", "List payout transactions"],
          ["GET", "/api/v1/transactions/service/payout/{transaction_id}", "Get single transaction details"]
        ]
      }
    ]
  }
];

export const BASE_URLS = {
  payin: "https://admin.klyropay.com",
  payout: "https://admin.klyropay.com",
};
